import React, { useState, useEffect, useCallback } from 'react';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';

const ImageSwapModal = ({ 
  isOpen, 
  onClose,  
  objectId,
  images,
  imageData,
  onImageSwapped,
  testImageOrder = null // テスト用：画像の順序を強制的に設定
}) => {
  // APIエラーハンドリングフック
  const {
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));

  // UI状態
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  
  // ドラッグ&ドロップ状態
  const [draggedIndex, setDraggedIndex] = useState(null); // ドラッグ中の画像インデックス
  const [dragOverIndex, setDragOverIndex] = useState(null); // ドロップ先の画像インデックス
  const [imageOrder, setImageOrder] = useState([]); // 画像の表示順序
  const [isDragging, setIsDragging] = useState(false); // ドラッグ中フラグ
  
  // ドラッグ位置情報
  // const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 }); // ドラッグ開始位置
  const [dragCurrentPos, setDragCurrentPos] = useState({ x: 0, y: 0 }); // 現在のドラッグ位置

  // モーダル初期化処理
  useEffect(() => {
    if (isOpen && images) {
      // テスト用の画像順序が指定されている場合はそれを使用
      const initialOrder = testImageOrder || images.map((_, index) => index);
      setImageOrder(initialOrder);
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  }, [isOpen, images, testImageOrder]);

  // ドラッグ開始処理
  const handleStart = (e, index) => {
    // passiveイベントリスナーではpreventDefault()が無効なので削除
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setDraggedIndex(index);
    setIsDragging(true);
    setDragCurrentPos({ x: clientX, y: clientY });
  };

  // ドラッグ移動処理
  const handleMove = useCallback((e) => {
    if (!isDragging || draggedIndex === null) return;
    
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setDragCurrentPos({ x: clientX, y: clientY });
    
    // ドロップ先の要素を検出
    const element = document.elementFromPoint(clientX, clientY);
    if (element) {
      const imageContainer = element.closest('[data-image-index]');
      if (imageContainer) {
        const dropIndex = parseInt(imageContainer.dataset.imageIndex);
        if (dropIndex !== draggedIndex) {
          setDragOverIndex(dropIndex);
        }
      }
    }
  }, [isDragging, draggedIndex]);

  // ドラッグ終了処理
  const handleEnd = useCallback((e) => {
    if (!isDragging || draggedIndex === null) return;
    
    e.preventDefault();
    
    if (dragOverIndex !== null && dragOverIndex !== draggedIndex) {
      const newOrder = [...imageOrder];
      const draggedItem = newOrder[draggedIndex];
      
      // ドラッグされたアイテムを削除
      newOrder.splice(draggedIndex, 1);
      
      // 新しい位置に挿入
      const newIndex = draggedIndex < dragOverIndex ? dragOverIndex - 1 : dragOverIndex;
      newOrder.splice(newIndex, 0, draggedItem);
      
      setImageOrder(newOrder);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  }, [isDragging, draggedIndex, dragOverIndex, imageOrder]);

  // グローバルイベントリスナー設定
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd, { passive: false });
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
    }
    
    return () => {
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
    };
  }, [isDragging, draggedIndex, dragOverIndex, handleMove, handleEnd]);

  // 画像入れ替え処理
  const handleSwapImages = async () => {
    if (imageOrder.length === 0) {
      MessageDialog.show('メッセージ', '画像がありません。');
      return;
    }

    // 元の順序と現在の順序を比較
    const originalOrder = images.map((_, index) => index);
    const hasChanged = imageOrder.some((item, index) => item !== originalOrder[index]);
    
    if (!hasChanged) {
      MessageDialog.show('メッセージ', '画像の順序が変更されていません。');
      return;
    }

    await MessageDialog.showConfirm('確認', '画像の順序を変更してもよろしいですか？', async () => {
      try {
        setIsLoading(true);

        const imageOrderList = imageOrder.map((originalIndex, newOrder) => ({
          objectImageId: imageData[originalIndex]?.objectImageId,
          orderIndex: newOrder
        })).filter(item => item.objectImageId !== undefined && item.objectImageId !== null);

        if (imageOrderList.length === 0) {
          MessageDialog.show('エラー', '画像がありません。', null, 'error');
          return;
        }

        const result = await mobileObjectDetailService.updateImageOrder(objectId, imageOrderList);

        if(result.returnCode !== '200'){
          throw new Error('画像の入れ替え処理に失敗しました。');
        }

        if (onImageSwapped) {
          onImageSwapped();
        }

        onClose();

      } catch (error) {
        handleApiError(new Error('画像の入れ替え処理に失敗しました。'));
        return;
      } finally {
        setIsLoading(false);
      }
    });
  };

  if (!isOpen) return null;

  const styles = {
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalContent: {
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      maxWidth: '90%',
      maxHeight: '90%',
      overflow: 'auto',
      width: '90%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '1px solid #ddd'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333'
    },
    closeButton: {
      background: '#666',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    imageList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      marginBottom: '20px',
      justifyContent: 'center',
      alignItems: 'center'
    },
    imageContainer: {
      position: 'relative',
      display: 'inline-block',
      cursor: 'grab',
      transition: 'all 0.2s ease',
      opacity: 1,
      touchAction: 'none',
      userSelect: 'none'
    },
    imageContainerDragging: {
      position: 'fixed',
      display: 'inline-block',
      cursor: 'grabbing',
      transform: 'scale(1.1) rotate(5deg)',
      opacity: 0.8,
      zIndex: 1000,
      pointerEvents: 'none',
      boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
    },
    imageContainerDragOver: {
      position: 'relative',
      display: 'inline-block',
      cursor: 'grab',
      transform: 'scale(1.05)',
      border: '2px dashed #007bff',
      borderRadius: '5px',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      transition: 'all 0.2s ease'
    },
    imageThumbnail: {
      width: '85px',
      height: '85px',
      objectFit: 'contain',
      borderRadius: '5px',
      backgroundColor: '#2B2222',
      display: 'block',
      userSelect: 'none'
    },
    swapButton: {
      width: '100%',
      background: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    swapButtonDisabled: {
      width: '100%',
      background: '#ccc',
      color: '#666',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'not-allowed',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    loadingText: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      margin: '20px 0'
    },
    instructionText: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      marginBottom: '15px'
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.header}>
          <div style={styles.title}>画像入れ替え</div>
          <button style={styles.closeButton} onClick={onClose}>
            戻る
          </button>
        </div>

        {isLoading ? (
          <div style={styles.loadingText}>画像を入れ替えています...</div>
        ) : (
          <>
            <div style={styles.instructionText}>
              画像をドラッグして順序を変更してください
            </div>
            
            <div style={styles.imageList}>
              {images && images.length > 0 ? (
                imageOrder.map((originalIndex, displayIndex) => {
                  const isDraggingItem = draggedIndex === displayIndex;
                  const isDragOver = dragOverIndex === displayIndex;
                  
                  const containerStyle = isDraggingItem 
                    ? {
                        ...styles.imageContainerDragging,
                        left: isDragging ? dragCurrentPos.x - 42.5 : 'auto',
                        top: isDragging ? dragCurrentPos.y - 42.5 : 'auto'
                      }
                    : isDragOver 
                      ? styles.imageContainerDragOver 
                      : styles.imageContainer;
                  
                  return (
                    <div 
                      key={`${originalIndex}-${displayIndex}`}
                      data-image-index={displayIndex}
                      style={containerStyle}
                      onTouchStart={(e) => handleStart(e, displayIndex)}
                      onMouseDown={(e) => handleStart(e, displayIndex)}
                    >
                      <img 
                        src={images[originalIndex]} 
                        alt={`画像 ${originalIndex + 1}`}
                        style={styles.imageThumbnail}
                        onError={(e) => {
                          e.target.src = '/core/img/errorimage.png';
                        }}
                        draggable={false}
                      />
                    </div>
                  );
                })
              ) : (
                <div style={styles.loadingText}>画像がありません</div>
              )}
            </div>

            <button 
              style={styles.swapButton}
              onClick={handleSwapImages}
              disabled={isLoading}
            >
              入れ替え登録
            </button>
          </>
        )}
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default ImageSwapModal;
